import { type ActorRefFrom, assign, createMachine, fromPromise } from 'xstate';
import { browserLogger } from '../../utils/loggers/browser';
import { logGtmEvent } from '../../utils/loggers/gtmLogger';
import type { CapitalizedBusinessLine } from '../../components/SocialButtons/SocialButtons';
import type { NameSchema } from '../../features/registration/components/NameForm';
import type { FormRegistrationError } from '../../features/registration/components/RegistrationForm';
import type { RegistrationResponseCreated, RegistrationSignupType } from '../../utils/requests/clientAuth/registration';
import { ONBOARDING_START_PATH } from '../../utils/values/paths';
import { gtmPrepareRegistrationData } from '../../utils/normalization/gtmPrepareRegistrationData';
import { createActorContextWithUseActor } from '../../utils/xstate/createActorContextWithUseActor';
import { clearIDmeSignupCookie } from '../../features/idme/utils';
import { sendGtmEvent } from '../../utils/helpers/sendGtmEvent';
export interface SignupContext {
  businessLine?: CapitalizedBusinessLine;
  email?: string;
  name?: NameSchema;
  zipCode?: string;
  isWafEnabled: boolean;
  /**
   * Set by /signup?default=true so signup experiments can skip allocation
   * and render the default experience for synthetics/non-experiment traffic.
   */
  isDefaultSignupExperience: boolean;
  signupType?: RegistrationSignupType;
  registrationResponse?: RegistrationResponseCreated;
}
type Data = {
  businessLine: CapitalizedBusinessLine;
} | {
  email: string;
} | {
  name: NameSchema;
} | {
  email: string;
  name: NameSchema;
};
type SignupEvent = {
  type: 'BACK';
} | {
  type: 'NEXT';
} | {
  type: 'NEXT';
  data: Data;
} | {
  type: 'SIGNUP_SUCCESS';
  registrationResponse: RegistrationResponseCreated;
} | {
  type: 'SIGNUP_FAILURE';
  error: FormRegistrationError;
} | {
  type: 'LOGIN_SUCCESS';
};
type SignupMachineInput = Partial<SignupContext>;
type SignupPanelName = 'businessLine' | 'email' | 'name';
const redirectFromSignupPanel = (context: SignupContext, panelName: SignupPanelName) => {
  if (panelName === 'businessLine') {
    window.history.back();
    return;
  }
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete('step');
  const params = searchParams?.size ? `?${searchParams}` : '';
  window.location.assign(`/signup${params}`);
};
const trackRegistrationSuccess = fromPromise(async ({
  input
}: {
  input: SignupContext;
}) => {
  const registrationResponse = input.registrationResponse;
  if (!registrationResponse) return;
  try {
    const {
      name,
      email,
      businessLine
    } = input;
    const hashedUserData = await gtmPrepareRegistrationData({
      firstName: name?.name,
      email,
      businessLine
    });
    const eventAttributes = {
      // This is the "legacy" tag, which triggers Facebook signup tracking
      business_line: businessLine,
      signUpType: registrationResponse.signUpType,
      onboarding_type: registrationResponse.onboardingType,
      onboardingType: registrationResponse.onboardingType,
      // This is a modern tag, which triggers most other things
      user: {
        businessLine: businessLine?.toLowerCase(),
        external_id: registrationResponse.externalId,
        externalId: registrationResponse.externalId,
        kid_external_id: registrationResponse.kidExternalId,
        kidExternalId: registrationResponse.kidExternalId,
        is_shell: registrationResponse.isShell,
        isShell: registrationResponse.isShell,
        ...hashedUserData
      },
      eventSource: 'growth-next-ui'
    };
    const result = await sendGtmEvent({
      eventName: 'sign-up',
      eventAttributes
    });
    logGtmEvent('sign-up', eventAttributes);
    if (result === 'timed_out') {
      browserLogger({
        level: 'warn',
        team: 'client-onboarding',
        feature: 'gtm-events',
        message: 'Timed out waiting for GTM sign-up event acknowledgement',
        context: {
          externalId: registrationResponse.externalId
        }
      });
    }
  } catch (error) {
    browserLogger({
      level: 'error',
      team: 'client-onboarding',
      feature: 'gtm-events',
      message: 'Failed to track sign-up event with GTM before redirect',
      context: {
        externalId: registrationResponse.externalId
      },
      error
    });
  }
});
export const signupMachine = createMachine({
  types: {} as {
    context: SignupContext;
    events: SignupEvent;
    input: SignupMachineInput;
  },
  /** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYFcAOA6WYANmAMYAuK6UAymltgPLYUD26AxAEICCAwgNIBtAAwBdRKGwtUrdBJAAPRAFoA7ADZcARgCcWgEw6AzABYArMKP71ADhMAaEAE9E+s6tzWjNrZfX+TNwBfIMdUDBx8IlIKKloIxmYUNnYAOQBRAA0AFRFxJBApGWS5AqUELSNhT2tKm31hVW9VGyNHFwqbXCszMxsdXtVVMxNVE3UQsLpIgmJyShppxNl2ABkGAHEASVSAfWoAVV5edOpqPPkilFl5cuUTGzNcf2H+1R1VYVqzdsR33Hq+i09SGpmEWjMWkmIHC9Cic1iiwSTBW1C2G1SBwACvsjiczhcClcbmUVPp6rhhMIzDoTFUBuojOoHM4-iZuqpKuYtE11OTRtDYTNovM4ksUSV2GiMdjdgAxbhbVYHABK6UJkmk1xKtxUoy6nPUZkZxhaRjM+l+CECTwhkLsJgMw3GJkFS1wYHQZDAACcFqkAIYAWzAXD4QjEly1JNA5WBTzcRgGlX0Gm5Vp5RlwgS0jNMqZ8AyMboSHq9vv9wdD0sxOMOx1O50jROjOtJCF6Olw7isVkC5mEjqtqn0AJ6-XG9UZ7xLcM93r9VEDIal6Nr8sVyrVGsKrbYuutfO6Jlp5PUelpOh0VpMg88ei+tPUY2NE2h6BYEDg8iF2CjxX3ds1GELt9H0IxgS0B4r2Ec8rWUMDbWfQI7FMI0LX0WdhQRBZ4noCVAM1ADSljFR-C0GoIJ8aCdFg69WQQZQ9GeM9-B0Z9qQsMwsLwWYYlw8UkjYXAfTAAMIA6IjtUIxQyMqSjIJouirQGbsRx5YEjGGPNi1CGF3T40UkXwoT0FwEgWCDbBiG9CB-2kkjZMYiDNAg-RAj8T5LFUK03ATW8jGaTSHnJHiywXSsQ3smMnO8Ud1C+fphBsWwdEMdR4OMbRkLA593N0YYwvnCslyrESxIk6K21I60qmeRLaJS-p0pUp5OwMOjwVvOwivLRcoGXMBzMs6ywFsqqZPKPp2S+ZlBwMNLwXojowK6djfAhR4bFm2wQhCIA */
  id: 'signup',
  description: `State machine that powers the Sign Up page flow`,
  initial: 'businessLine',
  context: ({
    input
  }) => ({
    isWafEnabled: false,
    isDefaultSignupExperience: false,
    ...(input as SignupMachineInput | undefined)
  }),
  states: {
    businessLine: {
      description: `user can select a business line`,
      on: {
        NEXT: {
          target: 'email',
          actions: 'assignDataToContext'
        },
        BACK: {
          actions: ({
            context
          }) => {
            redirectFromSignupPanel(context, 'businessLine');
          }
        }
      }
    },
    email: {
      description: `user can enter their email or register/login using SSO`,
      initial: 'ready',
      states: {
        ready: {},
        tracking: {
          invoke: {
            src: trackRegistrationSuccess,
            input: ({
              context
            }) => context,
            onDone: 'signupCompleted'
          }
        },
        signupCompleted: {
          entry: 'redirectToOnboarding',
          type: 'final'
        },
        loginCompleted: {
          entry: 'redirectHome',
          type: 'final'
        }
      },
      on: {
        BACK: {
          target: 'businessLine'
        },
        NEXT: {
          target: 'name',
          actions: 'assignDataToContext'
        },
        LOGIN_SUCCESS: {
          description: `SSO login success`,
          target: '.loginCompleted'
        },
        SIGNUP_SUCCESS: {
          description: `SSO sign up success`,
          target: '.tracking',
          actions: ['assignSignupSuccessData', 'clearIDmeSignupCookie']
        },
        SIGNUP_FAILURE: {
          description: `Email validation or SSO sign up failure`,
          actions: 'logError'
        }
      }
    },
    name: {
      description: `user can enter their name and submit the registration form`,
      initial: 'ready',
      states: {
        ready: {},
        tracking: {
          invoke: {
            src: trackRegistrationSuccess,
            input: ({
              context
            }) => context,
            onDone: 'completed'
          }
        },
        completed: {
          entry: 'redirectToOnboarding',
          type: 'final'
        }
      },
      on: {
        BACK: {
          target: 'email'
        },
        NEXT: {
          target: '.',
          actions: 'assignDataToContext'
        },
        SIGNUP_SUCCESS: {
          target: '.tracking',
          actions: ['assignSignupSuccessData', 'clearIDmeSignupCookie']
        },
        SIGNUP_FAILURE: {
          actions: 'logError'
        }
      }
    }
  }
}, {
  actions: {
    assignDataToContext: assign(({
      context,
      event
    }) => {
      if (event.type === 'NEXT' && 'data' in event) {
        return {
          ...context,
          ...event.data
        };
      }
      return context;
    }),
    assignSignupSuccessData: assign(({
      event
    }) => {
      if (event.type !== 'SIGNUP_SUCCESS') return {};
      return {
        registrationResponse: event.registrationResponse
      };
    }),
    logError: ({
      context,
      event
    }) => {
      if (event.type === 'SIGNUP_FAILURE') {
        browserLogger({
          level: 'info',
          feature: 'registration',
          message: 'Registration error - New onboarding',
          context: {
            error: event.error,
            isWafEnabled: context.isWafEnabled
          }
        });
        logGtmEvent('sign-up', {
          error: event.error ?? 'UNKNOWN_ERROR',
          eventSource: 'growth-next-ui'
        }, 'client-onboarding', 'error');
      }
    },
    redirectHome: () => window.location.assign('/'),
    redirectToOnboarding: () => window.location.assign(ONBOARDING_START_PATH),
    clearIDmeSignupCookie
  },
  guards: {}
});
export const SignupMachineContext = createActorContextWithUseActor(signupMachine);
export type SignupActor = ActorRefFrom<typeof signupMachine>;